/**
 * 事件发射器
 * 提供简单的发布/订阅模式实现
 */

/** 事件监听器类型 */
type Listener<T = any> = (payload: T) => void;

/**
 * 事件发射器类
 * 用于组件间的事件通信
 */
export class EventEmitter {
    /** 事件监听器映射表 */
    private events: Map<string, Listener[]> = new Map();

    /**
     * 订阅事件
     * @param event 事件名称
     * @param listener 事件处理函数
     * @returns 取消订阅的函数
     */
    public on(event: string, listener: Listener): () => void {
        if (!this.events.has(event)) {
            this.events.set(event, []);
        }
        this.events.get(event)!.push(listener);

        return () => this.off(event, listener);
    }

    /**
     * 取消订阅事件
     * @param event 事件名称
     * @param listener 要移除的事件处理函数
     */
    public off(event: string, listener: Listener): void {
        const listeners = this.events.get(event);
        if (!listeners) return;

        const index = listeners.indexOf(listener);
        if (index !== -1) {
            listeners.splice(index, 1);
        }
    }

    /**
     * 发送事件
     * @param event 事件名称
     * @param payload 事件数据
     */
    public emit(event: string, payload?: any): void {
        const listeners = this.events.get(event);
        if (listeners) {
            listeners.forEach(listener => {
                try {
                    listener(payload);
                } catch (error) {
                    console.error(`[EventEmitter] Error in listener for event "${event}":`, error);
                }
            });
        }
    }

    /** 清除所有事件监听器 */
    public clear(): void {
        this.events.clear();
    }
}
